import { Button, Color, Flex, Icon, Spacing, TableColumn, Text, Tooltip } from '@billy/design-system'
import { TFunction } from 'i18next'

import { SuggestedVatDropdown } from '../../../SuggestedVatDropdown'
import { SuggestionsTableAccessor } from '../../enums/SuggestionsTableAccessor'
import { SuggestionsTableRow } from '../../types/SuggestionsTableRow'
import { RejectAcceptHeader } from '../RejectAcceptHeader'
import { ScoreHeader } from '../ScoreHeader'
import { SelectedVat } from '../SelectedVat'
import { AcceptRejectButtons } from './elements/AcceptRejectButtons'

interface SuggestionsTableColumnsProps {
  baseCurrency: string
  handleAttachmentClick: (attachmentId: string) => void
  isRowReviewed: (rowId: string) => boolean
  handleReviewedChange: (rowId: string, checked: boolean) => void
  track: (event: string) => void
  t: TFunction
}

export const SuggestionsTableColumns = ({
  baseCurrency,
  handleAttachmentClick,
  isRowReviewed,
  handleReviewedChange,
  track,
  t,
}: SuggestionsTableColumnsProps): TableColumn<SuggestionsTableRow>[] => [
  {
    accessor: SuggestionsTableAccessor.Receipt,
    alignment: 'center',
    Cell: ({ value, row }) =>
      value ? (
        <Button
          variant="text"
          icon="paperclip"
          fullRound
          onClick={() => handleAttachmentClick(value)}
          disabled={isRowReviewed(row.original.id)}
        ></Button>
      ) : null,
    Header: t('copilot.table.receipt'),
    sortable: false,
    maxWidth: 45,
    fixedSize: true,
  },
  {
    accessor: SuggestionsTableAccessor.Description,
    Header: t('copilot.table.description'),
    Cell: ({ value }) => <Text>{value}</Text>,
    size: 'm',
  },
  {
    accessor: SuggestionsTableAccessor.Amount,
    Cell: ({ value }) => <Text>{value}</Text>,
    Header: t('copilot.table.amount', { currency: baseCurrency }),
    fixedSize: true,
    size: 's',
  },
  {
    accessor: SuggestionsTableAccessor.Account,
    Header: t('copilot.table.account'),
    Cell: ({ value }) => <Text>{value}</Text>,
    truncate: true,
    size: 'm',
  },
  {
    accessor: SuggestionsTableAccessor.Score,
    Header: <ScoreHeader />,
    headerName: t('copilot.table.score'),
    shouldWrapInText: false,
    Cell: ({ row, value: { rate, info } }) => {
      const isDisabled = isRowReviewed(row.original.id)
      return (
        <Tooltip label={info}>
          <Flex alignItems="center" gap={Spacing.S4}>
            <Text color={isDisabled ? Color.Grey200 : Color.Lavender700} weight="bold">
              {rate}%
            </Text>
            <Icon icon="info" color={isDisabled ? Color.Grey200 : Color.Lavender700} />
          </Flex>
        </Tooltip>
      )
    },
    fixedSize: true,
    size: 's',
  },
  {
    accessor: SuggestionsTableAccessor.CurrentVat,
    Header: t('copilot.table.current_vat'),
    Cell: ({ value }) => <SelectedVat value={value} />,
    fixedSize: true,
    maxWidth: 100,
  },
  {
    accessor: SuggestionsTableAccessor.SuggestedVat,
    Header: t('copilot.table.suggested_vat'),
    Cell: ({ value, row }) => (
      <SuggestedVatDropdown
        rowId={row.original.id}
        initialSuggestedRate={value}
        disabled={isRowReviewed(row.original.id)}
      />
    ),
    fixedSize: true,
    maxWidth: 120,
  },
  {
    accessor: SuggestionsTableAccessor.Approve,
    Header: <RejectAcceptHeader />,
    headerName: t('copilot.table.accept_or_reject'),
    alignment: 'center',
    Cell: ({ row }) => (
      <AcceptRejectButtons rowId={row.original.id} onReviewedChange={handleReviewedChange} track={track} t={t} />
    ),
    renderAsChildren: true,
    fixedSize: true,
    size: 's',
  },
]
